import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { GetRowIdParams } from "ag-grid-enterprise";
import { Button, message } from "antd";
import { SuccessMessages } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import CiqAgGrid from "components/ciq-ag-grid";
import CiqAgSearch from "components/ciq-ag-search";
import ErrorBoundary from "components/error-boundary";
import { useScRequestMaterialData } from "./hooks/use-sc-request-material-data";
import { useScRequestInvite } from "./hooks/use-sc-request-invite";
import { getScRequestColumnDefs, scRequestDefaultColDef } from "./columns";
import { ScRequestMaterial } from "./model";

const getRowId = (params: GetRowIdParams<ScRequestMaterial>) => params.data.id;

/**
 * Builds the `sendEstimateInviteData` array in the same shape used by the
 * existing request-durations flows (`{ assignee, notes, feature_instance_ids }`),
 * grouping the selected materials by their assignee. Rows without an assignee
 * can't be requested against and are skipped.
 */
function buildSendEstimateInviteData(rows: ScRequestMaterial[]) {
  const idsByAssignee = new Map<string, string[]>();
  rows.forEach((row) => {
    const assigneeId = row.assignee_user?.id;
    if (!assigneeId) return;
    const ids = idsByAssignee.get(assigneeId) ?? [];
    ids.push(row.id);
    idsByAssignee.set(assigneeId, ids);
  });

  return Array.from(idsByAssignee.entries()).map(
    ([assignee, featureInstanceIds]) => ({
      assignee,
      notes: "",
      feature_instance_ids: featureInstanceIds
    })
  );
}

/**
 * The read-only SC-request material log. Header carries only a title and a
 * client-side quick-filter search box; the grid is a plain, non-editable table
 * with no row selection, tool panels, export, or bulk actions.
 */
function ScRequestMaterialLog() {
  const { projectId } = useParams<{ projectId: string }>();
  const gridRef = useRef<AgGridReact<ScRequestMaterial>>(null);
  const [searchText, setSearchText] = useState("");
  const [selectedRows, setSelectedRows] = useState<ScRequestMaterial[]>([]);
  const [displayedCount, setDisplayedCount] = useState(0);

  const { entityList, loading, materialHeaderMap } = useScRequestMaterialData();
  const { sendInvite, sendingInvite } = useScRequestInvite();
  const { isProjectArchived } = useContext(ProjectContext);

  const columnDefs = useMemo(
    () => getScRequestColumnDefs(materialHeaderMap, projectId),
    [materialHeaderMap, projectId]
  );

  const onSearch = useCallback((text: string) => {
    setSearchText(text);
    gridRef.current?.api?.setGridOption("quickFilterText", text);
  }, []);

  const onSelectionChanged = useCallback(() => {
    setSelectedRows(gridRef.current?.api?.getSelectedRows() ?? []);
  }, []);

  // Keep the count badge in sync with the rows currently shown (reflects the
  // search box and column filters). onModelUpdated fires on load and whenever
  // the displayed row set changes.
  const onModelUpdated = useCallback(() => {
    setDisplayedCount(gridRef.current?.api?.getDisplayedRowCount() ?? 0);
  }, []);

  const onRequestLeadTimeStatus = useCallback(async () => {
    const sendEstimateInviteData = buildSendEstimateInviteData(selectedRows);
    if (!sendEstimateInviteData.length) {
      message.warning(
        "None of the selected materials have an assignee to request from."
      );
      return;
    }

    // Reuse the existing send-invite mutation, retaining sendEstimateInviteData
    // as-is and adding is_dummy: true at the same level (see mutations.ts).
    const result = await sendInvite({
      sendEstimateInviteData,
      is_dummy: true
    });

    // A GraphQL/network failure is already surfaced (error toast) by
    // useCIQMutation, so just bail and let the user retry — selection is kept.
    if (!result?.success) return;

    // Success but no token means we can't open the submission page — surface it
    // as an error rather than a misleading success, and keep the selection.
    const token = result.data?.send_estimate_invite?.response?.token;
    if (!token) {
      message.error(
        "The request was created but no submission link was returned. Please try again."
      );
      return;
    }

    // Open the lead-time submission page for the returned (dummy) invite token.
    // Token is passed through as-is (no URL encoding), per spec.
    window.open(`/submit-lead-time?token=${token}`, "_blank");
    message.success(SuccessMessages.RequestSentSuccess);
    // Selection is intentionally retained after a successful request.
  }, [selectedRows, sendInvite]);

  return (
    <div
      className="flex flex-col h-full w-full"
      data-testid="sc-request-material-log"
    >
      <div
        className="w-full px-6 py-4 flex justify-between items-center"
        data-testid="sc-request-header"
      >
        <div className="flex items-center space-x-2">
          <span className="text-black text-xl font-semibold">Materials</span>
          <span
            className="flex items-center border border-solid border-ciq-gray-300 rounded-xl px-2 py-0.5 text-black text-opacity-50 text-xs font-normal"
            data-testid="sc-request-count-badge"
          >
            {displayedCount}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            type="primary"
            onClick={onRequestLeadTimeStatus}
            disabled={!selectedRows.length || !!isProjectArchived}
            loading={sendingInvite}
            data-testid="sc-request-lead-time-status-button"
          >
            Request Lead Time/Status
            {selectedRows.length > 0 ? ` (${selectedRows.length})` : ""}
          </Button>
          <div data-testid="sc-request-search">
            <CiqAgSearch
              gridRef={gridRef}
              placeholder="Search Materials"
              value={searchText}
              onSearch={onSearch}
            />
          </div>
        </div>
      </div>

      <ErrorBoundary>
        <CiqAgGrid
          gridRef={gridRef}
          columnDefs={columnDefs}
          rowData={entityList}
          defaultColDef={scRequestDefaultColDef}
          rowHeight={62}
          getRowId={getRowId}
          loading={loading}
          rowSelection={{ mode: "multiRow", selectAll: "filtered" }}
          onSelectionChanged={onSelectionChanged}
          onModelUpdated={onModelUpdated}
          sideBar={false}
          // Keep the flex spacer column in charge of the leftover width; the
          // wrapper's resize-time sizeColumnsToFit would otherwise stretch the
          // data columns to fill.
          disableResizingAutoWidth
        />
      </ErrorBoundary>
    </div>
  );
}

export default ScRequestMaterialLog;
